import React, { useState, useMemo } from 'react';
import {
  Book, Search, ChevronRight, ChevronDown,
  Users, Briefcase, Calendar, DollarSign, Tag, Heart,
  CreditCard, Mail, MessageSquare, FileText, ShoppingBag,
  BarChart3, Star, Clock, Palette, Image, Building2,
  Bell, Shield, RefreshCw, Settings,
} from 'lucide-react';

/**
 * UserDocumentation — customer-facing documentation for MorphBook admins.
 *
 * Organized into accordion categories that map to actual product areas a
 * business owner uses day-to-day. Each section has a short description and
 * step-by-step "How to use" guidance written in plain language.
 *
 * Some sections (notably Payments) include sub-accordions for sub-topics like
 * individual payment providers. All sections are searchable.
 */

const SECTIONS = [
  // ─── Daily Operations ────────────────────────────────────────────────────
  {
    category: 'Daily Operations',
    icon: Calendar,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    items: [
      {
        id: 'clients',
        icon: Users,
        title: 'Clients',
        description: 'Manage your customers and their profile information.',
        steps: [
          'Go to the navigation bar and click **Clients**.',
          'To **add a client manually**: click **Add Client**, fill in their name, email, and phone, then click Save. The client receives an email invitation if email is configured.',
          'To **view a client**: click their row to see their full profile, appointment history, and any subjects (pets, vehicles, etc.) attached to their account.',
          'To **edit** a client: click the edit icon next to their name, make changes, and click Update.',
          'To **delete** a client: click the delete icon. You will be asked to confirm, since this also removes their appointments and history.',
          'Use the **search bar** at the top to find clients by name, email, or phone.',
        ],
        tips: [
          'Clients can also self-register through your booking page — you do not need to add them manually unless you want to.',
          'Contact information is used for appointment confirmations and reminders, so keep it accurate.',
        ],
      },
      {
        id: 'employees',
        icon: Briefcase,
        title: 'Employees',
        description: 'Manage staff accounts, roles, and access permissions.',
        steps: [
          'Go to the navigation bar and click **Employees**.',
          'To **add an employee**: click **Add Employee**, enter their name, email, phone, create a password, and select a role (employee or admin).',
          'To **edit an employee**: click the edit icon, update their info or role, and click Update.',
          'To **reset an employee password**: open their profile and click **Reset Password**, then set a new one.',
          'To **delete an employee**: click the delete icon. Their assigned appointments will need to be reassigned.',
          'Employees with the **employee** role have limited access. Employees with the **admin** role have full system access.',
        ],
        tips: [
          'Configure detailed permissions per employee in **Settings → Employee Permissions** if you want fine-grained control.',
          'Employees should change their password after first login.',
        ],
      },
      {
        id: 'booking-appointments',
        icon: Calendar,
        title: 'Booking Appointments',
        description: 'How appointments are booked, viewed, and managed.',
        steps: [
          'Go to the navigation bar and click **Appointments**.',
          'To **create an appointment**: click **Add Appointment**. Select the client, the subject (pet, vehicle, etc.), the service(s) and any add-ons, the assigned employee, and the date/time, then Save.',
          'To **edit an appointment**: click on it, update the details, and click Update. The client and employee receive notifications about the change.',
          'To **change status**: click the appointment and pick a new status — Pending, Confirmed, Completed, Cancelled, or Not Completed.',
          'To **filter** appointments: use the filter buttons at the top (status, date, employee).',
          'Clients book their own appointments through your booking page. Their requests appear here, and may need approval depending on your **Payment Workflow** setting.',
        ],
        tips: [
          'If a client misses or no-shows, mark it **Not Completed** rather than Cancelled — it preserves the history for reporting.',
          'Past appointments automatically update to Completed status when the appointment time passes (unless already marked otherwise).',
        ],
      },
      {
        id: 'time-off',
        icon: Clock,
        title: 'Time Off',
        description: 'Approve or deny employee time-off requests.',
        steps: [
          'Go to **Employees → Time Off Requests**.',
          'You see all pending, approved, and denied requests in one list.',
          'To **approve**: click **Approve**. The employee\'s schedule is automatically blocked for those dates and clients cannot book with them during that time.',
          'To **deny**: click **Deny**. Optionally add a reason — the employee sees this when they get the email notification.',
          'To **add admin notes**: open a request and write notes in the Admin Notes field. Notes are visible only to admins.',
          'Employees submit time-off requests from their own **Time Off** page. Approved time off is automatically reflected in availability.',
        ],
        tips: [
          'Set a clear policy on how far in advance employees should submit requests.',
          'Approved time off cannot be cancelled by the employee — they must contact you.',
        ],
      },
      {
        id: 'availability',
        icon: Clock,
        title: 'Availability & Capacity',
        description: 'Control when bookings are allowed and how many appointments can run at once.',
        steps: [
          'Go to **Settings → Availability** (or scroll within Settings to find Availability sections).',
          '**Business Operating Hours**: for each day of the week, set opening and closing times. Mark days the business is closed.',
          '**System-wide Capacity**: set the maximum number of overlapping appointments — e.g., 3 means up to 3 appointments can run at the same time.',
          '**Employee Schedules**: each employee can have their own working hours, which override the business-wide hours for them. Find this in the employee schedules section.',
          '**Shop Closure Dates**: add dates when the business is closed — holidays, vacations, weather. Clients cannot book on these dates.',
          'The booking page automatically reflects all of these settings — you don\'t need to update it separately.',
        ],
        tips: [
          'Set capacity realistically based on how many people you can serve at once, not the maximum possible.',
          'Block out time for breaks, cleaning, or admin tasks by adding shop closure entries during those windows.',
          'Update schedules well in advance for known holidays so clients don\'t try to book during closures.',
        ],
      },
      {
        id: 'messaging',
        icon: MessageSquare,
        title: 'Messaging',
        description: 'Send emails or SMS messages to clients and staff directly from the system.',
        steps: [
          'Go to the navigation bar and click **Messaging**.',
          'Select recipients — one or more clients, one or more employees, or use **Select All**.',
          'Choose delivery: **Send Email**, **Send SMS**, or both.',
          'Enter a subject (for email) and your message body.',
          'Click **Send Message**. Recipients are notified immediately.',
          'View past messages in the **Message History** section at the bottom of the page.',
        ],
        tips: [
          'SMS costs money per message — use it for important, time-sensitive things, not bulk marketing.',
          'Email is free in most setups — good for newsletters, promotions, and longer announcements.',
          'You must configure email and SMS settings before this feature works (see the Email Setup and SMS Setup sections).',
          'Always include opt-out information when sending marketing messages, especially via SMS.',
        ],
      },
      {
        id: 'statistics',
        icon: BarChart3,
        title: 'Statistics',
        description: 'View business performance, earnings, and trends.',
        steps: [
          'Go to the navigation bar and click **Stats** (or Statistics).',
          'Choose a period at the top: **Weekly**, **Monthly**, or **Yearly**.',
          'View earnings, appointment counts, and trends compared to the previous period.',
          'See **All-Time Stats** for lifetime totals.',
          'Check **Top Clients** to see who spends the most.',
          'View **Inventory Stats** if you sell Quick Buy products — see what\'s selling and what\'s low.',
          'Click **Download Report** to export the data as a CSV file you can open in Excel or Google Sheets.',
          'Use **Manual Payment Entry** to record cash, check, or other off-system payments so they appear in your stats.',
          'Use **Payment Logs** to view a detailed history of every payment processed through the system.',
        ],
        tips: [
          'Review weekly stats at the end of each week to spot trends early.',
          'Top Clients lets you reward loyal customers — consider offering them a discount or early access to new services.',
          'If your stats look wrong, make sure all in-person payments have been entered manually so they\'re counted.',
        ],
      },
    ],
  },

  // ─── Services & Catalog ──────────────────────────────────────────────────
  {
    category: 'Services & Catalog',
    icon: Tag,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    items: [
      {
        id: 'services-addons',
        icon: Tag,
        title: 'Services & Add-ons',
        description: 'Configure what you offer and how it\'s priced.',
        steps: [
          'Go to **Settings → Services & Pricing**.',
          'To **add a service**: click **Add Service**, enter the name, description, base price, and duration in minutes.',
          'To **add an add-on**: in the same area, look for **Add-ons** — these are extras clients can attach to a service (e.g., teeth brushing, deep conditioning, premium product).',
          'Each add-on has its own price and can add time to the appointment if needed.',
          'To **edit** a service or add-on: click the edit icon, change details, click Save.',
          'To **disable** without deleting: toggle the **Active** switch off. Disabled items are hidden from clients but kept for historical records.',
          'To **reorder**: drag and drop services into the order you want them shown to clients.',
        ],
        tips: [
          'Use clear, client-friendly names — "Premium Bath" reads better than "BATH-PRM-001".',
          'Add-ons are great for upselling without cluttering your main service list.',
          'Set durations honestly — they affect what time slots clients can book.',
        ],
      },
      {
        id: 'size-based-pricing',
        icon: DollarSign,
        title: 'Size-Based Pricing & Employee Capacities',
        description: 'Charge different prices for different subject sizes, and manage how many things each employee can handle at once.',
        steps: [
          '**Size-based pricing** lets you charge different rates depending on the subject (e.g., small dog vs. giant dog).',
          'Go to **Settings → Services & Pricing**.',
          'For each service, enter prices for each size tier (Small, Medium, Large, Giant by default — you can rename these).',
          'When a client books, the system uses the subject\'s size to pick the right price automatically.',
          '**Employee capacities** control how many simultaneous appointments each employee can handle.',
          'Go to **Settings → Employee Capacity** (or the schedules section).',
          'Set each employee\'s max simultaneous appointments — e.g., 1 if they only handle one subject at a time, 3 if they can run multiple in parallel.',
          'You can also override system-wide capacity for specific employees who work faster or slower.',
        ],
        tips: [
          'If you don\'t use size-based pricing, just set the same price across all tiers — clients won\'t notice.',
          'Capacity should reflect reality, not aspiration. Overbooking damages reputation faster than empty slots.',
        ],
      },
      {
        id: 'subjects',
        icon: Heart,
        title: 'Subjects',
        description: 'Manage what your services apply to — pets, vehicles, properties, or anything else.',
        steps: [
          '**Subjects** are the things your service is performed on. For dog grooming, that\'s pets. For lawn care, that\'s yards or vehicles. The system is fully configurable.',
          'Go to **Settings → Subject Configuration**.',
          'Set the **singular name** (e.g., "Pet", "Vehicle", "Property") and **plural name** (e.g., "Pets", "Vehicles", "Properties").',
          'Pick an **icon** (emoji) that represents the subject.',
          'Decide whether subjects are **required for booking** — if yes, clients must select or add one when booking.',
          'Customize the **fields** clients fill out about each subject (name, breed, weight, color, model year, address, etc.) — see the Client Form Fields section for details.',
          'You can disable subjects entirely if your service doesn\'t need them — toggle **Subject Enabled** off.',
        ],
        tips: [
          'Choose terminology your clients will understand — "Pet" for groomers, "Vehicle" for car detailers, "Property" for cleaners.',
          'Keep the field list short — clients abandon long forms.',
        ],
      },
      {
        id: 'quick-buy',
        icon: ShoppingBag,
        title: 'Quick Buy Products',
        description: 'Sell retail products alongside your services.',
        steps: [
          'Go to the navigation bar and click **Quick Buy** (admin view).',
          'To **add a product**: click **Add Product**. Enter name, description, price, stock quantity, category, and upload an image.',
          'Mark **Available** to make the product visible to clients. Uncheck to hide temporarily without deleting.',
          'To **edit**: click the edit icon, update details, click Update.',
          'To **track inventory**: update stock quantity as you sell. Set to 0 when out of stock.',
          'Clients see Quick Buy in their navigation. They can browse, add to cart, and pay through your configured payment provider.',
          'Sales appear in your **Statistics** under inventory and revenue.',
        ],
        tips: [
          'Use high-quality product images — they significantly increase conversion.',
          'Bundle products with services where it makes sense (e.g., shampoo with a bath service).',
          'Restock popular items before they run out — empty product pages frustrate clients.',
        ],
      },
      {
        id: 'recommendations',
        icon: Star,
        title: 'Recommendations',
        description: 'Suggest other businesses to your clients — partner services, vets, suppliers, etc.',
        steps: [
          'Go to **Settings → Other Settings → Manage Recommendations**.',
          'To **add a recommendation**: click **Add Recommendation**. Enter the business name, category, description, contact info, address, and optionally upload a logo.',
          'Mark **Featured** to highlight key partners at the top of the list.',
          'To **edit**: click the edit icon, update info, click Update.',
          'Clients see your recommendations under their **Recommendations** menu — useful for cross-promotion with complementary businesses.',
        ],
        tips: [
          'Only recommend businesses you trust — a bad recommendation reflects on you.',
          'Ask partners for reciprocal recommendations — it builds a referral network.',
          'Keep contact info current; outdated phone numbers frustrate clients.',
        ],
      },
    ],
  },

  // ─── Payments ────────────────────────────────────────────────────────────
  {
    category: 'Payments',
    icon: CreditCard,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    items: [
      {
        id: 'payment-overview',
        icon: CreditCard,
        title: 'Payment System Overview',
        description: 'How payments work in MorphBook — providers, workflows, and permissions.',
        steps: [
          'Go to **Settings → Payment Settings**.',
          'You can enable any combination of providers: **Pay in Person**, **Stripe**, **PayPal**, and **Square**.',
          'Clients choose their preferred method at checkout from whichever providers you have active.',
          'Each provider has its own setup steps (see the sub-sections below for each).',
          'Configure global behavior using the **Payment Workflow** setting:',
          '  • **Pay First (default)**: client pays immediately when booking. If you decline the appointment later, the system issues an automatic refund.',
          '  • **Approve First**: client books without paying, you approve the appointment, then the client receives a payment link.',
          'Set **payment permissions** per role — clients, employees, admins. You can also disable client online payments while still letting employees take payments in person.',
          'Enable **down payments** if you want clients to pay a portion of the total upfront (non-refundable) and the remainder at the appointment. Optionally enable **installments** so clients can split the full payment into multiple chunks.',
        ],
        subSections: [
          {
            id: 'pay-in-person',
            title: 'Pay in Person',
            steps: [
              'On the Pay in Person card, click **Activate**. That\'s it — no configuration needed.',
              'Clients booking through the system will see "Pay at appointment" as an option.',
              'Record the payment manually after the appointment — go to **Statistics → Manual Payment Entry** or mark it from the appointment itself.',
              'To stop accepting in-person payments, click **Deactivate** on the same card.',
            ],
          },
          {
            id: 'stripe',
            title: 'Stripe',
            steps: [
              'Stripe is the easiest online provider to set up — most businesses are live in 30 minutes.',
              'See the full **STRIPE_SETUP.md** guide in your docs folder for step-by-step instructions including test cards and live activation.',
              'In short:',
              '  1. Sign up at [stripe.com](https://dashboard.stripe.com/register) (free, no fees until you take a payment).',
              '  2. Get your **Publishable key** (`pk_test_...`) and **Secret key** (`sk_test_...`) from Stripe Dashboard → Developers → API keys.',
              '  3. In Payment Settings, click **Configure** on the Stripe card, paste both keys, and Save.',
              '  4. Click **Activate** on the Stripe card.',
              '  5. Test with card `4242 4242 4242 4242`, any future date, any CVC.',
              '  6. When ready, activate your Stripe account, switch to Live keys (`pk_live_`/`sk_live_`), and update your settings.',
              'Stripe charges 2.9% + $0.30 per online transaction in the US (rates vary by country).',
            ],
          },
          {
            id: 'square',
            title: 'Square',
            steps: [
              'Square is a great choice if you also use Square for in-person sales — your reporting is unified.',
              'See the full **SQUARE_SETUP.md** guide in your docs folder for detailed step-by-step instructions.',
              'In short:',
              '  1. Sign up at [developer.squareup.com](https://developer.squareup.com/signup).',
              '  2. Create an application in the Developer Dashboard.',
              '  3. From the Sandbox tab, copy your **Application ID**, **Access Token**, and **Location ID**.',
              '  4. In Payment Settings, click **Configure** on the Square card, paste all three, set Environment to **Sandbox**, and Save.',
              '  5. Click **Activate** on the Square card.',
              '  6. Test with card `4111 1111 1111 1111`, any future date, any CVV.',
              '  7. When ready, activate your Square account, switch to Production credentials, change Environment to Production, and update.',
              'Square charges 2.9% + $0.30 per online transaction in the US.',
            ],
          },
          {
            id: 'paypal',
            title: 'PayPal',
            steps: [
              'PayPal lets clients pay with their PayPal balance, linked bank, or card via PayPal.',
              'See the full **PAYPAL_SETUP.md** guide in your docs folder for detailed step-by-step instructions.',
              'In short:',
              '  1. Sign up at [developer.paypal.com](https://developer.paypal.com).',
              '  2. Create a Sandbox app to get your test **Client ID** and **Secret**.',
              '  3. In Payment Settings, click **Configure** on the PayPal card, paste both, set Mode to **Sandbox**, and Save.',
              '  4. Click **Activate** on the PayPal card.',
              '  5. Test with the sandbox personal account PayPal provides.',
              '  6. When ready, create a Live app, switch to Live Client ID/Secret, change Mode to **Live**.',
              'PayPal\'s fees are similar to Stripe and Square — typically 2.9% + $0.49 per transaction in the US.',
            ],
          },
          {
            id: 'down-payments',
            title: 'Down Payments (Non-Refundable)',
            steps: [
              '**Important terminology**: a **down payment** is non-refundable — the client pays it ahead of time and it does not come back to them. This is different from a *deposit*, which implies a refund. MorphBook uses "down payment" everywhere client-facing for this reason.',
              'Go to **Settings → Payment Settings**. Down Payments is a top-level green card above the provider grid (you don\'t configure it per-provider; the same setting applies to Stripe, PayPal, Square, and Pay in Person).',
              'Toggle **Allow down payments at booking** on. Once enabled, clients see a "Pay Down Payment" option at checkout alongside "Pay Full Amount" and "Pay in Person".',
              'Choose the **default down-payment amount type**:',
              '  • **Percent of total** — e.g., 50%, so a $200 service requires a $100 down payment. Useful when prices vary across services.',
              '  • **Fixed dollar amount** — e.g., $50 down regardless of total. Easier to communicate to clients ("a $50 down payment is required for any booking"). Capped automatically at the appointment total if the total is lower.',
              'Optionally enable **"Let admin set the down-payment amount per appointment"**. When ON, you can override the default amount per-appointment (e.g., charge a smaller down payment for a returning regular). When OFF, every client pays the configured default.',
              'Optionally write a **custom non-refundable notice** — the text shown to clients next to the down-payment option at checkout. Leave blank to use the default: *"Down payments are non-refundable. They are paid ahead of time and do not come back to you."*',
              'Click **Save Settings**. The values are synced to all configured providers automatically.',
            ],
            tips: [
              'The non-refundable notice appears in two places: the client booking flow (next to the radio when they select "Pay Down Payment") and the admin Record Down Payment modal (so you see exactly what the client sees).',
              'When admin override is on, the **Record Partial Payment** button on an unpaid appointment opens in **Down Payment Mode** — green header, suggested amount pre-filled, "Use suggested" button, and the editable amount field. When override is off, the field is locked to the configured default and a lock icon explains why.',
              'After-the-fact partial payments (recording cash a client paid you in person, after they\'ve already paid something) still work — the modal switches to its original blue UX whenever the appointment already has any payment recorded against it.',
              'Down payments are recorded as `payment_status = partial` on the appointment until the remaining balance is paid. The remaining balance is collected at the appointment via Pay in Person or another method.',
              'Pay in Person doesn\'t need a separate down-payment toggle — if a client picks Pay in Person, they pay the full amount when they arrive, no down payment is collected.',
            ],
          },
          {
            id: 'installments',
            title: 'Installments (Pay in Chunks)',
            steps: [
              '**What this is**: lets clients split the full payment into multiple chunks at booking. Different from a down payment — the client still pays the full total, just over time rather than all at once.',
              'Go to **Settings → Payment Settings**. Installments is a top-level blue card directly below Down Payments.',
              'Toggle **Allow clients to pay in installments** on.',
              'Set the **Maximum number of installments** — clients can pick any value from 2 up to this number. A reasonable cap is 2–4 for most service businesses.',
              'Click **Save Settings**.',
              'Note on per-chunk payment methods: when fully shipped, the client will be able to pick a different payment method per chunk — e.g., "$50 online via Stripe today, $50 cash at the appointment". This makes installments work even for clients without enough on a card right now.',
            ],
            tips: [
              '**Status as of May 2026 (Phase 2a + 2b shipped)**: the full installments flow is live end-to-end. Clients see "Pay in Installments" at checkout, pick chunk count, and set each chunk\'s amount + method (online or in person). The first online chunk is paid at booking; in-person chunks are paid in cash at the appointment.',
              '**Manage Installments modal**: every partial-paid appointment row has a sky-blue **Manage Installments** button. The modal lists every chunk with paid/pending/overdue badges and per-chunk actions: **Mark paid (cash)** for in-person chunks, **Mark paid (manual)** for any online chunk you collected outside the system, and **Send link** for pending online chunks #2+.',
              '**Send link** generates a single-purpose secure URL (7-day expiry) and emails it to the client. The client opens the link, picks a payment method (Stripe credit card or PayPal — Square clients can still use the existing Square Payment Link admin action), pays without logging in, and the chunk auto-marks paid. If your SMTP isn\'t configured the email won\'t send, but the modal shows the URL with a Copy button so you can share it via SMS or your own channel.',
              '**Resend link** rotates the token: any previously-sent email link stops working. Use this if a client says they lost the email or the original expired.',
              '**Phase 2b still pending**: automatic reminder schedule (nudging clients before each chunk\'s due date). Until that ships, you can send/resend links on demand from the Manage Installments modal.',
              'Installments and down payments are **independent** — you can enable one, both, or neither. A client could in principle pay a down payment AND have the remaining balance split into installments, though typically you\'d use one or the other.',
              'Unlike down payments, **installment chunks are refundable** if the appointment is cancelled before the chunk is paid. Already-paid chunks follow your standard refund/cancellation policy.',
            ],
          },
          {
            id: 'admin-accept-flow',
            title: 'Approve-First Workflow (Admin Reviews Before Payment)',
            steps: [
              'By default MorphBook uses **Pay First**: the client pays immediately when booking, and the appointment is created.',
              'If you prefer to review and approve appointments before the client pays, switch the **Payment Workflow** setting in Payment Settings to **Approve First**.',
              'In Approve-First mode: the client books without paying, the appointment is created with `payment_status = pending`, and you receive a notification.',
              'Open the appointment, review it, and either approve or decline. On approval, the client receives a payment link via email/SMS. On decline, the appointment is cancelled with no payment taken.',
              'Useful for businesses that screen clients before accepting (e.g., aggressive-pet evaluation, custom quotes).',
            ],
            tips: [
              'Approve-First works with all payment options including down payments and installments — the link the client receives reflects whatever they selected at booking.',
              'If you decline an appointment after the client already paid (Pay-First mode), MorphBook attempts an automatic refund through the payment provider.',
            ],
          },
        ],
        tips: [
          'Always test in Sandbox/Test mode before going live with real customer payments.',
          'You can run multiple providers simultaneously — clients pick at checkout.',
          'Stripe is the simplest start. Add more providers later if customers ask for them.',
        ],
      },
    ],
  },

  // ─── Forms & Customization ───────────────────────────────────────────────
  {
    category: 'Forms & Customization',
    icon: Palette,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    items: [
      {
        id: 'client-form-fields',
        icon: FileText,
        title: 'Client Form Fields',
        description: 'Customize what information clients are asked when registering or booking.',
        steps: [
          'Go to **Settings → Form Fields → Client Fields** (or use the Form Fields Manager).',
          'You see the default fields — name, email, phone — and any custom fields you\'ve added.',
          'To **add a custom field**: click **Add Field**. Choose:',
          '  • **Field name** (internal, no spaces — e.g., `address`)',
          '  • **Field label** (what clients see — e.g., "Home Address")',
          '  • **Field type**: Text, Email, Phone, Text Area, Number, Date, Dropdown, or Checkbox',
          '  • **Placeholder** text (optional hint shown in the empty field)',
          '  • Whether the field is **Required**',
          '  • Whether it\'s **Active** (active fields show on the form)',
          'For Dropdown fields, add the options clients can pick from.',
          'Drag fields to reorder how they appear on the form.',
        ],
        tips: [
          'Don\'t over-collect. Every extra field reduces signup completion.',
          'Make required fields actually required — only ask for what you genuinely need.',
          'Standard contact info (name, email, phone) is enough for most service businesses.',
        ],
      },
      {
        id: 'subject-form-fields',
        icon: FileText,
        title: 'Subject Form Fields',
        description: 'Customize what information is collected about each subject (pet, vehicle, etc.).',
        steps: [
          'Go to **Settings → Subject Configuration**.',
          'Scroll to the **Fields** section.',
          'Add custom fields for whatever your service needs to know — breed, weight, color, year, address, special notes, allergies.',
          'Same field types are available: Text, Email, Phone, Text Area, Number, Date, Dropdown, Checkbox.',
          'Mark fields as **Required** if the booking flow needs them.',
          'Drag to reorder.',
          'These fields appear when a client adds or edits a subject (pet/vehicle/etc.).',
        ],
        tips: [
          'Special notes is usually the most-used field — keep it as a Text Area near the bottom.',
          'For dog grooming, common useful fields: Breed, Weight, Coat Type, Behavioral Notes, Allergies.',
          'For vehicle services: Make, Model, Year, License Plate, Color.',
        ],
      },
      {
        id: 'site-design',
        icon: Palette,
        title: 'Site Design & Branding',
        description: 'Customize colors, logo, fonts, and the look of your booking site.',
        steps: [
          'Go to **Site Design** in the navigation bar.',
          '**Site Name**: change the business name shown in the header and emails.',
          '**Logo**: choose Emoji or Image. For Image, upload a file (PNG or JPG recommended).',
          '**Colors**: use the color pickers to set logo background, navbar background, and text colors. Changes preview in real-time.',
          '**Login Page**: customize title, subtitle, and how logos are positioned on the login screen.',
          '**Navigation Labels**: rename menu items if your industry uses different terminology (e.g., change "Clients" to "Customers" or "Members").',
          '**Fonts**: pick a font family for navigation, headers, and body text.',
          'Click **Save Branding** at the bottom.',
          'Refresh your browser to see all changes take effect.',
        ],
        tips: [
          'Stick to 2-3 colors total — too many looks chaotic.',
          'Use a high-resolution logo (at least 500px wide) so it looks crisp on retina screens.',
          'Test your design on mobile — many clients book from their phones.',
        ],
      },
      {
        id: 'homepage',
        icon: Image,
        title: 'Homepage Customization',
        description: 'Edit the public homepage that clients see.',
        steps: [
          'Go to **Site Design** → scroll to **Hero Section**.',
          'Edit the **Main Title** and **Subtitle** — what clients see when they land on your site.',
          'Style the title — choose font, size (12-96px), and color with the color picker.',
          'Style the subtitle — same options at smaller scale.',
          'Click **Save Hero Section**.',
          'Scroll to **Why Choose Us? Features** — edit three feature cards (title and description for each).',
          'Click **Save Features**.',
          'Upload custom **hero background images** in the appropriate section. Adjust overlay and opacity to keep text readable over the image.',
        ],
        tips: [
          'Lead with a clear value proposition, not "Welcome to [Business]".',
          'Subtitle should answer: what do you do, for whom, and why are you better?',
          'Use real photos of your work, not stock images, when possible.',
        ],
      },
      {
        id: 'business-info',
        icon: Building2,
        title: 'Business Information',
        description: 'Set your business address, hours, contact details, and tax rates.',
        steps: [
          'Go to **Settings → Business Information**.',
          'Enter your **Business Name**, **Address**, **Phone**, and **Public Email**.',
          'Set your **Tax Rate** — applied to invoices and statistics where relevant.',
          'Add a **Business Description** — used on the homepage and in some emails.',
          'Upload **business images** for branding consistency.',
          'Save changes at the bottom.',
          'Business hours are set separately under **Settings → Availability** (see Availability section above).',
        ],
        tips: [
          'Keep this information up-to-date — it\'s used in invoices, emails, and on your booking page.',
          'Tax settings depend on your jurisdiction — consult an accountant if unsure.',
        ],
      },
    ],
  },

  // ─── Communications ──────────────────────────────────────────────────────
  {
    category: 'Communications',
    icon: Mail,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    items: [
      {
        id: 'email-setup',
        icon: Mail,
        title: 'Email Setup',
        description: 'Configure email so the system can send appointment confirmations, reminders, and notifications.',
        steps: [
          'Go to **Settings → Communication Settings → Email**.',
          'Enter your email server details (SMTP). Your email provider (Gmail, Outlook, your hosting provider) gives you these:',
          '  • **SMTP Host** — e.g., `smtp.gmail.com`, `smtp.office365.com`, or `smtp.yourdomain.com`',
          '  • **SMTP Port** — usually `587` (TLS) or `465` (SSL)',
          '  • **Username** — typically your full email address',
          '  • **Password** — your email password OR an **app password** (recommended for Gmail and Outlook; see provider docs for how to generate one)',
          '  • **From Email** — what shows in the "From" line of outgoing emails (usually the same as your username)',
          '  • **From Name** — what clients see as the sender name (e.g., "Happy Tails Grooming")',
          'Click **Save**.',
          'Click **Test Email** and send a test to yourself to verify everything works.',
          'Once tested, the system automatically sends:',
          '  • Appointment confirmations when clients book',
          '  • Reminders before appointments (configured in Notifications)',
          '  • Status updates when appointments change',
          '  • Password resets and account verification',
        ],
        tips: [
          '**Use an app password, not your real password** for Gmail or Outlook — it\'s more secure and won\'t break when you change your real password.',
          'If emails go to spam, ask your email provider about setting up SPF and DKIM records — improves deliverability dramatically.',
          'For high volume, consider a transactional email service like SendGrid, Mailgun, or Amazon SES — more reliable than personal email accounts.',
        ],
      },
      {
        id: 'sms-setup',
        icon: MessageSquare,
        title: 'SMS Setup',
        description: 'Configure SMS so the system can send text message reminders and notifications.',
        steps: [
          'Go to **Settings → SMS Settings**.',
          'Choose your SMS provider — **Twilio**, **Vonage**, or **AWS Pinpoint**. Each has its own setup guide in your docs folder.',
          'Sign up with your chosen provider (links below) and obtain credentials:',
          '  • **Twilio** — Account SID, Auth Token, and a phone number you purchase from Twilio',
          '  • **Vonage** (formerly Nexmo) — API Key, API Secret, and a sender ID or phone number',
          '  • **AWS Pinpoint** — AWS Access Key, Secret Key, Region, and a Pinpoint application',
          'Enter your provider\'s credentials in the SMS Settings page.',
          'Click **Save**, then **Test SMS** — send a test to your own phone to verify.',
          'Activate the provider — only one SMS provider can be active at a time.',
          'Once active, the system can send SMS reminders and messaging based on your **Notification Settings**.',
        ],
        tips: [
          '**Twilio** is the easiest to start with and well-documented.',
          '**Vonage** is often cheaper internationally.',
          '**AWS Pinpoint** is best if you already use AWS for other things, but more complex to set up.',
          'SMS costs money per message — typically $0.0075-$0.04 per message in the US, more internationally.',
          'Always test thoroughly before sending to real customers — sending broken SMS damages trust.',
          'Detailed provider-specific guides are in your **docs** folder: `SMS_SETUP_GUIDE.md`, `AWS_PINPOINT_SETUP.md`, `SMS_PROVIDER_COMPARISON.md`.',
        ],
      },
      {
        id: 'notifications',
        icon: Bell,
        title: 'Notifications',
        description: 'Configure when and how the system notifies clients and staff automatically.',
        steps: [
          'Go to **Settings → Notification Settings**.',
          '**Appointment Reminders**: enable to automatically remind clients before their appointment. Set how many hours before (e.g., 24 hours).',
          'Choose delivery method — Email, SMS, or both. SMS requires SMS Setup to be done first.',
          '**Customize Templates**: edit the wording of automatic notifications. Use placeholders like `{clientName}`, `{appointmentDate}`, `{serviceName}` — they get replaced with real values when sent.',
          '**Notification Types** you can configure:',
          '  • Booking confirmation',
          '  • Appointment reminder',
          '  • Status change (confirmed, cancelled, completed)',
          '  • Receipt after payment',
          '  • Time-off request status (for employees)',
          '  • Booking notifications (for employees and admins)',
          'Test each notification type to make sure it sends correctly.',
        ],
        tips: [
          '24 hours before is a good default reminder timing — far enough out to reschedule, close enough to remember.',
          'Don\'t over-notify. Two reminders for the same appointment annoys clients.',
          'Keep templates short and clear. Long messages get ignored.',
          'Always include the date, time, and service in reminders — that\'s what clients want to know.',
        ],
      },
    ],
  },

  // ─── System & Security ───────────────────────────────────────────────────
  {
    category: 'System & Security',
    icon: Shield,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    items: [
      {
        id: 'security',
        icon: Shield,
        title: 'Security Settings',
        description: 'Protect your admin account with two-factor authentication and recovery options.',
        steps: [
          'Go to **Settings → Security**.',
          '**Recovery Email**: set a backup email different from your main login email — used if you lose access to your account.',
          '**Recovery Phone**: set a backup phone number for SMS-based recovery codes.',
          '**Two-Factor Authentication (2FA)**: turn this on. It adds a code (sent by email, SMS, or both) on top of your password.',
          'Choose your 2FA method — Email, SMS, or both. SMS is more secure for high-stakes accounts but requires SMS Setup.',
          'Generate **backup codes** and store them somewhere safe (password manager, printed copy in a safe). These let you log in if you lose your phone or email access.',
          '**Activity Log**: review recent logins and account changes. If you see something you don\'t recognize, change your password immediately.',
        ],
        tips: [
          'Turn on 2FA. Stolen passwords are the #1 cause of business-software breaches and 2FA stops most of them.',
          'Use unique passwords for every system. A password manager (1Password, Bitwarden) makes this easy.',
          'Backup codes are essential — without them, losing your 2FA device can lock you out for days.',
          'Review the activity log monthly.',
        ],
      },
      {
        id: 'user-management',
        icon: Users,
        title: 'User Management',
        description: 'Manage all user accounts — admins, employees, and clients — in one place.',
        steps: [
          'Most user management happens in dedicated areas — **Clients** for clients, **Employees** for employees.',
          'Use the unified User Management screen for cross-cutting tasks:',
          '  • Reset any user\'s password',
          '  • Change a user\'s role (e.g., promote an employee to admin)',
          '  • Disable an account temporarily without deleting it',
          '  • Delete an account permanently',
          '  • View a user\'s activity log',
          'Find this in **Settings → User Management** or via the Password Reset Manager.',
        ],
        tips: [
          'Disable rather than delete when an employee leaves — preserves their history for reporting.',
          'Reset passwords through the system rather than asking for them — never know a user\'s password.',
          'Limit admin role to people who genuinely need it. The fewer admins, the smaller the security surface.',
        ],
      },
      {
        id: 'updates',
        icon: RefreshCw,
        title: 'Updates',
        description: 'Keep your booking system current with the latest features and security fixes.',
        steps: [
          'Go to **Updates** in the admin navigation.',
          'You see the current version installed and whether a newer version is available.',
          'When an update is available, review the **release notes** to see what\'s new and any breaking changes.',
          'Click **Update Now** to install. The system applies the update automatically.',
          'After updating, your data is preserved — clients, appointments, settings, payments, everything.',
          'If an update fails, you can roll back to the previous version from the same screen.',
        ],
        tips: [
          'Update during off-hours when no clients are likely to be booking.',
          'Read release notes — sometimes a feature you wanted is in a new release.',
          'Always update for security releases promptly — they patch vulnerabilities.',
        ],
      },
    ],
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

const renderInline = (text) => {
  // Render **bold** segments inline. Keep it simple — no full markdown.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
    }
    // Render markdown links [text](url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const segments = [];
    let lastIndex = 0;
    let match;
    while ((match = linkRegex.exec(part)) !== null) {
      if (match.index > lastIndex) segments.push(part.slice(lastIndex, match.index));
      segments.push(
        <a
          key={`${i}-${match.index}`}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:underline"
        >
          {match[1]}
        </a>
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < part.length) segments.push(part.slice(lastIndex));
    return segments.length > 0 ? <React.Fragment key={i}>{segments}</React.Fragment> : part;
  });
};

const matchesQuery = (item, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  if (item.title.toLowerCase().includes(q)) return true;
  if (item.description.toLowerCase().includes(q)) return true;
  if (item.steps?.some(s => s.toLowerCase().includes(q))) return true;
  if (item.tips?.some(t => t.toLowerCase().includes(q))) return true;
  if (item.subSections?.some(ss =>
    ss.title.toLowerCase().includes(q) ||
    ss.steps?.some(s => s.toLowerCase().includes(q))
  )) return true;
  return false;
};

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

const UserDocumentation = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(() => {
    // Open the first category by default; others closed.
    return SECTIONS.reduce((acc, s, i) => ({ ...acc, [s.category]: i === 0 }), {});
  });
  const [expandedItems, setExpandedItems] = useState({});
  const [expandedSubSections, setExpandedSubSections] = useState({});

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const toggleItem = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSubSection = (id) => {
    setExpandedSubSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // When the user searches, auto-expand matches.
  const filteredSections = useMemo(() => {
    if (!searchQuery) return SECTIONS;
    return SECTIONS
      .map(section => ({
        ...section,
        items: section.items.filter(item => matchesQuery(item, searchQuery)),
      }))
      .filter(section => section.items.length > 0);
  }, [searchQuery]);

  const totalItems = SECTIONS.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <Book className="text-primary-600" size={28} />
          <h2 className="text-2xl font-bold text-gray-900">Documentation</h2>
        </div>
        <p className="text-gray-600">
          Step-by-step guides for every feature of your booking system. Click any topic to expand.
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder={`Search ${totalItems} topics...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        {searchQuery && (
          <p className="text-sm text-gray-500 mt-2">
            Showing matches for &ldquo;{searchQuery}&rdquo;
            {filteredSections.length === 0 && ' — no results'}
          </p>
        )}
      </div>

      {/* Categories */}
      <div className="space-y-4">
        {filteredSections.map(section => {
          const CategoryIcon = section.icon;
          const isCategoryExpanded = !!searchQuery || expandedCategories[section.category];

          return (
            <div
              key={section.category}
              className={`border-2 rounded-lg overflow-hidden ${section.borderColor} bg-white`}
            >
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(section.category)}
                className={`w-full px-5 py-4 flex items-center justify-between transition-colors ${section.bgColor} hover:opacity-90`}
              >
                <div className="flex items-center space-x-3">
                  <CategoryIcon className={section.color} size={24} />
                  <div className="text-left">
                    <h3 className={`text-lg font-bold ${section.color}`}>
                      {section.category}
                    </h3>
                    <p className="text-xs text-gray-600">
                      {section.items.length} topic{section.items.length === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
                {isCategoryExpanded ? (
                  <ChevronDown className={section.color} size={22} />
                ) : (
                  <ChevronRight className="text-gray-400" size={22} />
                )}
              </button>

              {/* Category Items */}
              {isCategoryExpanded && (
                <div className="divide-y divide-gray-200">
                  {section.items.map(item => {
                    const ItemIcon = item.icon;
                    const isExpanded = !!searchQuery || expandedItems[item.id];

                    return (
                      <div key={item.id}>
                        <button
                          onClick={() => toggleItem(item.id)}
                          className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <ItemIcon className="text-gray-500 flex-shrink-0" size={20} />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900">{item.title}</h4>
                              <p className="text-sm text-gray-600 truncate">{item.description}</p>
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronDown className="text-gray-400 flex-shrink-0 ml-2" size={18} />
                          ) : (
                            <ChevronRight className="text-gray-400 flex-shrink-0 ml-2" size={18} />
                          )}
                        </button>

                        {isExpanded && (
                          <div className="px-5 pb-5 pt-1 space-y-4 bg-gray-50">
                            <p className="text-gray-700">{item.description}</p>

                            {/* Steps */}
                            {item.steps && item.steps.length > 0 && (
                              <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <h5 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                                  <Book size={16} className="text-primary-600" />
                                  <span>How to use</span>
                                </h5>
                                <ol className="space-y-2 list-decimal list-inside marker:text-gray-400">
                                  {item.steps.map((step, i) => (
                                    <li key={i} className="text-sm text-gray-700 leading-relaxed pl-1">
                                      {renderInline(step)}
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            )}

                            {/* Sub-sections */}
                            {item.subSections && item.subSections.length > 0 && (
                              <div className="space-y-2">
                                {item.subSections.map(sub => {
                                  const isSubExpanded = !!searchQuery || expandedSubSections[`${item.id}-${sub.id}`];
                                  return (
                                    <div
                                      key={sub.id}
                                      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                                    >
                                      <button
                                        onClick={() => toggleSubSection(`${item.id}-${sub.id}`)}
                                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                                      >
                                        <span className="font-medium text-gray-900">{sub.title}</span>
                                        {isSubExpanded ? (
                                          <ChevronDown className="text-gray-400" size={16} />
                                        ) : (
                                          <ChevronRight className="text-gray-400" size={16} />
                                        )}
                                      </button>
                                      {isSubExpanded && sub.steps && (
                                        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                                          <ol className="space-y-2 list-decimal list-inside marker:text-gray-400">
                                            {sub.steps.map((step, i) => (
                                              <li key={i} className="text-sm text-gray-700 leading-relaxed pl-1">
                                                {renderInline(step)}
                                              </li>
                                            ))}
                                          </ol>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Tips */}
                            {item.tips && item.tips.length > 0 && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h5 className="font-semibold text-blue-900 mb-2 flex items-center space-x-2">
                                  <Star size={16} />
                                  <span>Tips</span>
                                </h5>
                                <ul className="space-y-1.5 list-disc list-inside marker:text-blue-400">
                                  {item.tips.map((tip, i) => (
                                    <li key={i} className="text-sm text-blue-900 leading-relaxed pl-1">
                                      {renderInline(tip)}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 text-center">
        Need more detailed setup guides for payment providers or SMS? Look in your project&rsquo;s
        {' '}<code className="bg-white px-2 py-0.5 rounded border border-gray-200">docs/</code> folder
        for reference markdown files.
      </div>
    </div>
  );
};

export default UserDocumentation;
